import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  SxProps,
} from "@mui/material";
import { Cancel, Delete, Edit, Save } from "@mui/icons-material";
import ChallengeTeamSelector from "./ChallengeTeamSelector";
import InterruptingChallengeSelector from "./InterruptingChallengeSelector";
import Grid from "@mui/material/Grid";
import SetSelector from "./SetSelector";
import ScoreSelector from "./ScoreSelector";
import ChallengeTypeSelector from "./ChallengeTypeSelector";
import ChallengeResultSelector from "./ChallengeResultSelector";
import { useSelectedChallengeContext } from "@context/SelectedChallengeContext";
import { useGameDataContext } from "@context/GameDataContext";
import { PdfChallenge, TeamReference } from "@/models/pdfData";
import { formatScore } from "@context/utils";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { gameState } from "@/services/gameState";
import { SuccessfulChallenge } from "@/models/gameState";

const EditChallenge: FC = () => {
  const {
    challenge,
    setSetScoreBeforeRally,
    setSetScoreBeforeChallenge,
    setSetScoreAfterChallenge,
    newChallenge,
  } = useSelectedChallengeContext();
  const { game, addChallenge, deleteChallenge } = useGameDataContext();
  const currentScore = gameState("currentScore");
  const getCurrentScoreForTeam = gameState("getCurrentScoreForTeam");
  const { onMobile, onTablet } = useResponsiveContext();

  const teamInterruptedGame = challenge.challengeMoment === "DURING_RALLY";

  useEffect(() => {
    if (
      teamInterruptedGame &&
      !challenge?.setScoreBeforeRally &&
      currentScore
    ) {
      setSetScoreBeforeRally(currentScore);
    } else if (!teamInterruptedGame && challenge?.setScoreBeforeChallenge) {
      const intendedTeam1 =
        challenge.setScoreBeforeChallenge.team1 -
        (challenge?.challenger === "team2" ? 1 : 0);
      const intendedTeam2 =
        challenge.setScoreBeforeChallenge.team2 -
        (challenge?.challenger === "team1" ? 1 : 0);

      if (
        challenge.setScoreBeforeRally?.team1 !== intendedTeam1 ||
        challenge.setScoreBeforeRally?.team2 !== intendedTeam2
      ) {
        setSetScoreBeforeRally({ team1: intendedTeam1, team2: intendedTeam2 });
      }
    }

    if (challenge?.setScoreBeforeChallenge) {
      const { setScoreBeforeChallenge, successful, challenger, unresolved } =
        challenge;

      let intendedAfterTeam1 = setScoreBeforeChallenge.team1;
      let intendedAfterTeam2 = setScoreBeforeChallenge.team2;

      if (teamInterruptedGame) {
        if (unresolved) {
          intendedAfterTeam1 += challenger === "team2" ? 1 : 0;
          intendedAfterTeam2 += challenger === "team1" ? 1 : 0;
        } else if (challenger === "referee" && successful) {
          intendedAfterTeam1 +=
            (challenge as SuccessfulChallenge).outcome === "POINT_TEAM1"
              ? 1
              : 0;
          intendedAfterTeam2 +=
            (challenge as SuccessfulChallenge).outcome === "POINT_TEAM2"
              ? 1
              : 0;
        }
      } else {
        if (successful && challenger !== "referee") {
          if ((challenge as SuccessfulChallenge).outcome === "POINT_REVERSED") {
            intendedAfterTeam1 += challenger === "team1" ? 1 : -1;
            intendedAfterTeam2 += challenger === "team2" ? 1 : -1;
          } else {
            intendedAfterTeam1 -= challenger === "team1" ? 0 : 1;
            intendedAfterTeam2 -= challenger === "team2" ? 0 : 1;
          }
        }
      }

      if (
        challenge.setScoreAfterChallenge?.team1 !== intendedAfterTeam1 ||
        challenge.setScoreAfterChallenge?.team2 !== intendedAfterTeam2
      ) {
        setSetScoreAfterChallenge({
          team1: intendedAfterTeam1,
          team2: intendedAfterTeam2,
        });
      }
    }
  }, [currentScore, challenge, teamInterruptedGame]);

  const saveChallenge = () => {
    const missingInputs = Object.entries(challenge)
      .filter(([key, value]) => !value && key != "scoreRallyEnd" && key != "id")
      .map(([key]) => key);
    if (missingInputs.length <= 0 && game) {
      // addChallenge(game.samsId, );
      newChallenge();
    } else {
      console.debug(missingInputs);
    }
  };

  const removeChallenge = () => {
    if (challenge?.id) {
      deleteChallenge(challenge.id);
      newChallenge();
    }
  };

  const centeredGridSx: SxProps = {
    display: "flex",
    justifyContent: "center",
  };

  return (
    <Box
      maxHeight={"100%"}
      height={"100%"}
      boxSizing={"border-box"}
      pt={onTablet ? 0 : 3}
      pb={onMobile ? 0 : 1}
      px={onMobile ? 0 : 2}
      overflow={"auto"}
    >
      <Grid container columns={{ xs: 8, md: 8, lg: 8 }} rowGap={3}>
        <Grid size={{ xs: 8, md: 8, lg: 3 }} sx={centeredGridSx}>
          <ChallengeTeamSelector />
        </Grid>
        <Grid size={{ xs: 8, md: 8, lg: 5 }} sx={centeredGridSx}>
          <InterruptingChallengeSelector />
        </Grid>
        <Grid size={{ xs: 8, md: 8, lg: 2 }} sx={centeredGridSx}>
          <SetSelector />
        </Grid>
        <Grid size={{ xs: 8, md: 4, lg: 3 }} sx={centeredGridSx}>
          <ScoreSelector
            disabled={!teamInterruptedGame}
            score={challenge?.setScoreBeforeRally}
            setScore={setSetScoreBeforeRally}
            title={"Punktestand Anpfiff"}
          />
        </Grid>
        <Grid size={{ xs: 8, md: 4, lg: 3 }} sx={centeredGridSx}>
          <ScoreSelector
            greyedOut={teamInterruptedGame}
            score={challenge?.setScoreBeforeChallenge}
            setScore={setSetScoreBeforeChallenge}
            title={"Punktestand Abpfiff"}
          />
        </Grid>
        <Grid size={8}>
          <Divider />
        </Grid>
        <Grid size={{ xs: 8, md: 4, lg: 5 }} sx={centeredGridSx}>
          <ChallengeTypeSelector />
        </Grid>
        <Grid size={{ xs: 8, md: 4, lg: 3 }} sx={centeredGridSx}>
          <ScoreSelector
            disabled={true}
            score={challenge?.setScoreAfterChallenge}
            setScore={setSetScoreAfterChallenge}
            title={"Punktestand nach Challenge"}
          />
        </Grid>
        <Grid size={8} sx={centeredGridSx}>
          <ChallengeResultSelector />
        </Grid>
      </Grid>
      <Box display={"flex"} gap={3} justifyContent={"center"}>
        <Button
          sx={{ mt: 3 }}
          variant={"contained"}
          onClick={() => saveChallenge()}
          startIcon={<Save />}
        >
          Speichern
        </Button>
        <Button
          sx={{ mt: 3 }}
          variant={"contained"}
          color={"warning"}
          onClick={() => newChallenge()}
          startIcon={<Cancel />}
        >
          Abbrechen
        </Button>
        {game?.challenges.find(({ id }) => id === challenge?.id) && (
          <Button
            sx={{ mt: 3 }}
            variant={"contained"}
            color="error"
            startIcon={<Delete />}
            onClick={() => removeChallenge()}
          >
            Löschen
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EditChallenge;
